/**
 * VersionTimeline — horizontal node chain for the Lineage lens.
 *
 * Calls `GET /api/v1/generation/briefs/{brief_id}/version-chain` and renders
 * v1 → v2 → … → current as clickable circles.  The current version is
 * highlighted in teal; other versions are dimmed.  Click to navigate to
 * that version's workspace.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Loader2 } from 'lucide-react';
import { generationApi, type BriefVersionNode } from '@/services/api';

interface Props {
  briefId: string;
  /** Pre-seed with the current version's status so we can show something
   *  before the chain fetch resolves. */
  fallbackVersion?: number;
  fallbackStatus?: string;
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  active: 'bg-teal-50 text-teal-700 border-teal-200',
  superseded: 'bg-amber-50 text-amber-700 border-amber-200',
  archived: 'bg-slate-50 text-slate-400 border-slate-200',
};

export function VersionTimeline({
  briefId,
  fallbackVersion,
  fallbackStatus,
}: Props) {
  const [chain, setChain] = useState<BriefVersionNode[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    generationApi
      .getVersionChain(briefId)
      .then((res) => {
        if (!cancelled) setChain(res.chain);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message || 'Failed to load chain');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [briefId]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <GitBranch size={14} className="text-slate-400" />
        <h3 className="text-sm font-bold text-slate-800">Version timeline</h3>
        {loading && (
          <Loader2 size={11} className="text-slate-300 animate-spin ml-auto" />
        )}
      </div>

      {error && (
        <div className="text-[12px] text-rose-600">{error}</div>
      )}

      {!loading && !error && chain && chain.length > 0 && (
        <Timeline chain={chain} />
      )}

      {!loading && !error && (!chain || chain.length === 0) && (
        <FallbackSingleVersion version={fallbackVersion} status={fallbackStatus} />
      )}
    </div>
  );
}

function Timeline({ chain }: { chain: BriefVersionNode[] }) {
  return (
    <div className="flex items-start gap-0 overflow-x-auto py-2">
      {chain.map((node, i) => (
        <NodeWrapper key={node.brief_id} isLast={i === chain.length - 1}>
          <Node node={node} />
        </NodeWrapper>
      ))}
    </div>
  );
}

function NodeWrapper({
  children,
  isLast,
}: {
  children: React.ReactNode;
  isLast: boolean;
}) {
  return (
    <>
      {children}
      {!isLast && (
        <div className="w-12 h-px bg-slate-200 mt-6 flex-shrink-0" />
      )}
    </>
  );
}

function Node({ node }: { node: BriefVersionNode }) {
  const navigate = useNavigate();
  const formatted = formatDate(node.created_at);
  const statusCls =
    STATUS_COLORS[node.status] ?? 'bg-slate-50 text-slate-500 border-slate-200';

  return (
    <button
      type="button"
      onClick={() =>
        navigate(`/creative/briefs/${node.brief_id}?lens=lineage`)
      }
      className={`flex flex-col items-center gap-1 px-3 cursor-pointer group transition ${
        node.is_current ? '' : 'opacity-70 hover:opacity-100'
      }`}
      title={node.name}
    >
      <div
        className={`w-12 h-12 rounded-full border-2 flex items-center justify-center text-sm font-bold transition ${
          node.is_current
            ? 'bg-teal-50 border-teal-400 text-teal-700 shadow-sm'
            : 'bg-white border-slate-300 text-slate-500 group-hover:border-slate-400'
        }`}
      >
        v{node.version}
      </div>
      {formatted && (
        <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
          {formatted}
        </span>
      )}
      <span
        className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${statusCls}`}
      >
        {node.status}
      </span>
    </button>
  );
}

function FallbackSingleVersion({
  version,
  status,
}: {
  version?: number;
  status?: string;
}) {
  if (!version) {
    return (
      <p className="text-[11px] text-slate-400">No version chain available.</p>
    );
  }
  const cls = STATUS_COLORS[status ?? ''] ?? 'bg-slate-50 text-slate-500 border-slate-200';
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="font-bold text-brand-purple">v{version} · current</span>
      {status && (
        <span
          className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${cls}`}
        >
          {status}
        </span>
      )}
    </div>
  );
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}
